import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { Layout } from './Layout';
import { useSession } from './SessionContext';
import { Administration } from '../pages/Administration';
import { AVenir } from '../pages/AVenir';
import { Clients } from '../pages/Clients';
import { Connexion } from '../pages/Connexion';
import { Introuvable } from '../pages/Introuvable';
import { Invitation } from '../pages/Invitation';
import { MonCompte } from '../pages/MonCompte';
import { MotDePasse } from '../pages/MotDePasse';
import { Journal } from '../pages/Journal';
import { MesInterventions } from '../pages/MesInterventions';
import { Rapports } from '../pages/Rapports';
import { TableauDeBord } from '../pages/TableauDeBord';

/**
 * Garde des écrans : sans session, on renvoie vers la connexion en gardant
 * l'adresse demandée. L'écran de connexion est le SEUL public — c'est le serveur
 * qui refuse les données, pas l'interface qui fait semblant.
 */
function Protege({ children }: { readonly children: ReactNode }) {
  const { utilisateur, chargement } = useSession();
  const emplacement = useLocation();

  if (chargement) {
    return (
      <p role="status" className="p-8 text-sm text-slate-500">
        Vérification de la session…
      </p>
    );
  }

  if (!utilisateur) {
    return <Navigate to="/connexion" replace state={{ depuis: emplacement.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * Garde de rôle : un écran d'administration n'est pas caché à un prestataire, il
 * lui est REFUSÉ explicitement. Le serveur refuserait de la même façon (403).
 */
function ProtegeAdmin({ children }: { readonly children: ReactNode }) {
  const { utilisateur } = useSession();

  if (utilisateur?.role !== 'admin') {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6" role="alert">
        <p className="font-medium text-slate-800 mb-1">Écran réservé aux administrateurs</p>
        <p className="text-sm text-slate-600">
          Votre compte n’a pas ce rôle. Demandez à un administrateur de vous l’accorder si votre
          poste l’exige.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/connexion" element={<Connexion />} />
      {/* Page PUBLIQUE : le compte n'existe pas encore, aucune session requise. */}
      <Route path="/invitation/:jeton" element={<Invitation />} />
      {/* Réinitialisation : publique elle aussi, c'est l'accès perdu qu'on répare. */}
      <Route path="/mot-de-passe/:jeton" element={<MotDePasse />} />
      <Route
        element={
          <Protege>
            <Layout />
          </Protege>
        }
      >
        <Route index element={<TableauDeBord />} />
        <Route path="interventions" element={<MesInterventions />} />
        <Route path="rapports" element={<Rapports />} />
        <Route path="clients" element={<Clients />} />
        <Route path="mon-compte" element={<MonCompte />} />
        <Route
          path="administration"
          element={
            <ProtegeAdmin>
              <Administration />
            </ProtegeAdmin>
          }
        />
        <Route
          path="journal"
          element={
            <ProtegeAdmin>
              <Journal />
            </ProtegeAdmin>
          }
        />
        <Route path="a-venir/:sujet" element={<AVenir />} />
        <Route path="*" element={<Introuvable />} />
      </Route>
    </Routes>
  );
}
